import React, { useState } from "react";
import {
    type ConstructionSystem,
    type MeasureForm,
} from "@treegram/viewer-core";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import Button from "@/components/buttons/button";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import LoadingButton from "@/components/buttons/loading-button";
import { BaseDialogProps } from "@/components/dialogs/base-dialog";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type";
import MeasureTypeEditor from "./measure-type-editor";
import addMeasureTypeAction from "./add-measure-type-action";

interface Props extends BaseDialogProps {
    measureTypeAddedCallback: (type: MeasureType) => void;
}
export default function AddMeasureTypeDialog({
    open,
    onClose,
    measureTypeAddedCallback,
}: Props) {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { predefinedShapes, projectMeasureTypes, addMeasureType } =
        useProjectMeasureContext();
    const [shape, setShape] = useState<MeasureForm>("Line");
    const [system, setSystem] = useState<ConstructionSystem>("none");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string>();
    const close = () => {
        onClose(false);
        setError(undefined);
        setSystem("none");
    };
    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!ensureCanEdit()) {
            return;
        }

        setPending(true);
        setError(undefined);
        try {
            const result = await addMeasureTypeAction({
                formData: new FormData(event.currentTarget),
                predefinedShapes,
                projectMeasureTypes,
                addMeasureType,
            });
            if (result.success && result.addedMeasureType) {
                measureTypeAddedCallback(result.addedMeasureType);
                close();
            } else setError(result.message);
        } catch (e) {
            setError(
                e instanceof Error ? e.message : "Enregistrement impossible.",
            );
        } finally {
            setPending(false);
        }
    };
    return (
        <ClosableDialog
            open={open}
            onClose={close}
            title="Nouveau type de métré"
            sizeClass="max-w-xl max-h-[90vh]"
            closeOnOutsideClick={!pending}
            showCloseButton={!pending}
        >
            <form onSubmit={submit} className="flex min-h-0 flex-col">
                {error && (
                    <div
                        role="alert"
                        className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
                    >
                        {error}
                    </div>
                )}
                <fieldset
                    disabled={pending}
                    className="min-h-0 overflow-y-auto"
                >
                    <MeasureTypeEditor
                        disabled={!canEdit || (pending)}
                        shape={shape}
                        system={system}
                        availableShapes={predefinedShapes.map(
                            (item) => item.name,
                        )}
                        values={{
                            constructionSystem: system,
                            measureForm: shape,
                        }}
                        onShapeChange={(value) => {
                            setShape(value);
                            setSystem("none");
                        }}
                        onSystemChange={setSystem}
                    />
                </fieldset>
                <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4">
                    <Button
                        variant="ghost"
                        size="md"
                        disabled={pending}
                        onClick={close}
                    >
                        Annuler
                    </Button>
                    <LoadingButton disabled={!canEdit}
                        variant="success"
                        size="md"
                        type="submit"
                        isLoading={pending}
                        loadingBody="Enregistrement…"
                        body="Ajouter"
                    />
                </div>
            </form>
        </ClosableDialog>
    );
}
